import { NextPage } from 'next';
import Head from 'next/head';
import { useCallback, useEffect } from 'react';
import { CATEGORIES } from '../../misc-types/category';
import BusinessCardGrid from '../../react-components/business/card-grid';
import CompactLocationPicker from '../../react-components/inputs/compact-location-picker';
import Select from '../../react-components/inputs/select';
import Layout from '../../react-components/layout';
import OfferGrid from '../../react-components/offer/grid';
import SpecialCardGrid from '../../react-components/special/card-grid';
import Spinner from '../../react-components/spinner';
import { useUrl } from '../../utils/hooks';
import { switchRequestState, useRequest } from '../../utils/make-api-request';
import { SearchResults } from '../api/search-results';

type SearchResultsRequestParameters = Record<'lat' | 'lng' | 'category' | 'searchSpecials', string>;

const getCategoryName = (slug: string) => CATEGORIES.find(c => c.slug === slug)?.name;

/**
 * Searches for results from given data.
 * @param data
 * @param initialSearchResults preloaded search results from server to hydrate
 */
function useSearchResults(categorySlug: string, searchSpecials: boolean, geolocation?: google.maps.LatLngLiteral) {
  const [searchResultsRequest, getSearchResults] = useRequest<'get', SearchResults>('search-results', 'get');
  const loadResults = useCallback(() => {
    if (geolocation?.lat && geolocation?.lng && categorySlug && searchSpecials !== undefined) {
      const params: SearchResultsRequestParameters = {
        lat: `${geolocation?.lat}`,
        lng: `${geolocation?.lng}`,
        category: categorySlug,
        searchSpecials: `${searchSpecials}`
      };
      getSearchResults(params);
    }
  }, [categorySlug, searchSpecials, geolocation?.lat, geolocation?.lng, getSearchResults]);
  useEffect(() => {
    loadResults();
  }, [loadResults]);
  return [searchResultsRequest, loadResults] as const;
}

const Find: NextPage = () => {
  const { slug, searchParams, mutate: mutateUrl } = useUrl();
  const categorySlug = slug === 'specials' ? searchParams.get('category') ?? 'any' : slug;
  const lat = parseInt(searchParams.get('lat') ?? '');
  const lng = parseInt(searchParams.get('lng') ?? '');
  const geolocation = Number.isNaN(lat) || Number.isNaN(lng) ? undefined : { lat, lng };
  const [searchResultsRequest, reloadResults] = useSearchResults(categorySlug, slug === 'specials', geolocation);
  const pageTitle =
    slug === 'specials'
      ? categorySlug === 'any'
        ? 'Search Specials'
        : `Search Specials: ${getCategoryName(categorySlug)}`
      : `Search: ${getCategoryName(categorySlug)}`;
  return (
    <Layout>
      <Head>
        <title>{pageTitle}</title>
        <meta
          name='description'
          content={`Find ${
            slug === 'specials'
              ? categorySlug === 'any'
                ? 'special deals'
                : `special deals on ${getCategoryName(categorySlug)?.toLowerCase()}`
              : getCategoryName(categorySlug)?.toLowerCase()
          } from businesses near you on FitSociety.`}
        />
      </Head>
      <div className='top-section flex flex-wrap -m-1 items-center mb-4'>
        <h1 className='select-none'>{pageTitle}</h1>
        <div className='ml-auto flex flex-wrap items-start'>
          {slug === 'specials' && (
            <Select
              option={categorySlug === 'any' ? 'All categories' : getCategoryName(categorySlug) ?? 'Unknown category'}
              options={['All categories', ...CATEGORIES.filter(c => c.type === 'offer').map(c => c.name)]}
              labelText='Filter by category'
              className='change-input-background mr-2 mb-1'
              setOption={async option => {
                const newCategorySlug =
                  option === 'All categories' ? 'any' : CATEGORIES.find(c => c.name === option)?.slug ?? 'any';
                mutateUrl(
                  '/find/[slug]',
                  url => {
                    url.searchParams.set('category', newCategorySlug);
                    return url;
                  },
                  'replace',
                  true
                );
              }}
            />
          )}
          <form className='change-input-background' onSubmit={e => e.preventDefault()}>
            <CompactLocationPicker
              alignDropdown='right'
              onChange={async ({ lat: newLat, lng: newLng }) => {
                mutateUrl(
                  '/find/[slug]',
                  url => {
                    url.searchParams.set('lat', newLat.toString());
                    url.searchParams.set('lng', newLng.toString());
                    return url;
                  },
                  'replace',
                  true
                );
              }}
              eager={geolocation == null}
            />
          </form>
        </div>
      </div>
      {switchRequestState(
        searchResultsRequest,
        data =>
          data.results.length === 0 ? (
            <div className='text-center select-none'>No results were found.</div>
          ) : data.resultType === 'offer' ? (
            <OfferGrid offers={data.results} reload={reloadResults} />
          ) : data.resultType === 'business' ? (
            <BusinessCardGrid businesses={data.results} />
          ) : data.resultType === 'special' ? (
            <SpecialCardGrid specials={data.results} />
          ) : null,
        error => (
          <div className='text-center'>An error occurred; try again.</div>
        ),
        <div className='flex justify-center'>
          <Spinner />
        </div>
      )}
    </Layout>
  );
};

const noSSR =
  <P, IP = P>(Component: NextPage<P, IP>, DefaultComponent: React.FC): typeof Component =>
  props => {
    return typeof window !== 'undefined' ? <Component {...props} /> : <DefaultComponent />;
  };

export default noSSR(Find, function ServerSideFindPageStub() {
  return (
    <Layout>
      <Head>
        <title>Search</title>
      </Head>
    </Layout>
  );
});
